import type { MenuItem } from 'primereact/menuitem';
import { Steps } from 'primereact/steps';
import type { Ref } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from '../button/button';
import type { WizardStepProps } from './wizard-step.component';

export interface WizardProps {
  title: string;
  children: Array<React.ReactElement<WizardStepProps>>;
  activeStepIndex?: number;
  disableFinishButton?: boolean;
  // An optional action to execute when the wizard is canceled
  onCancel?: () => void;
  // An optional action to execute when the wizard is completed
  onFinish?: () => void;
  // Whether the wizard should close when the escape key is pressed. Defaults to false.
  closeOnEscape?: boolean;
}

export interface WizardRef {
  open: () => void;
  close: () => void;
}

const Wizard = (
  {
    title,
    children,
    activeStepIndex = 0,
    onCancel,
    onFinish,
    disableFinishButton = false,
    closeOnEscape = false,
  }: WizardProps,
  ref: Ref<WizardRef>
) => {
  const [index, setIndex] = useState<number>(activeStepIndex);

  const visibleChildren = useMemo(() => {
    return children.filter((item) => !item.props.hideStep);
  }, [children]);

  const { onBack, onNext } = visibleChildren[index].props;

  const modalRef = useRef<HTMLDialogElement>(null);

  const { t } = useTranslation();

  const stepLabels: MenuItem[] = useMemo(() => {
    return visibleChildren.map((wizardStep) => {
      return {
        label: wizardStep.props.label ?? '',
      };
    });
  }, [visibleChildren]);

  useEffect(() => {
    const currentModal = modalRef.current;
    return () => {
      currentModal?.close();
    };
  }, []);

  const open = () => {
    modalRef.current?.showModal();
  };

  const close = () => {
    modalRef.current?.close();
  };

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const handleDialogClose = () => {
    setIndex(0);
  };

  const handleDialogCancel = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (closeOnEscape) {
        close();
        onCancel?.();
      }
    },
    [closeOnEscape, onCancel]
  );

  const handleCancel = () => {
    close();
    onCancel?.();
  };

  const handleBack = () => {
    if (!onBack || onBack() === true) {
      setIndex(Math.max(index - 1, 0));
    }
  };

  const handleNext = () => {
    if (!onNext || onNext() === true) {
      setIndex(Math.min(index + 1, visibleChildren.length - 1));
    }
  };

  const handleFinish = () => {
    close();
    onFinish?.();
  };

  const createDialog = () => {
    return (
      <dialog
        ref={modalRef}
        data-testid="wizard-dialog"
        onCancel={handleDialogCancel}
        onClose={handleDialogClose}
        className="outline-hidden rounded-md p-4 w-[800px] h-[600px] px-2 overflow-hidden"
      >
        <div className="text-base font-medium text-mono-0 pb-4 px-2 pt-0">{title}</div>
        <Steps className="odin w-full pb-4 px-2" model={stepLabels} activeIndex={index} />
        <div className="flex px-2 flex-grow w-full h-full min-h-[100px] max-h-[400px] text text-gray-2 overflow-y-auto custom-scroll">
          {visibleChildren.map((child, childIndex) => {
            return (
              <div key={stepLabels[childIndex].label} hidden={childIndex !== index} className="w-full">
                {child}
              </div>
            );
          })}
        </div>
        <div className="flex pt-2 w-full gap-4 justify-end px-2">
          <Button onClick={handleCancel}>{t('cancel', 'Cancel')}</Button>
          <Button disabled={index < 1} onClick={handleBack}>
            {t('back', 'Back')}
          </Button>
          <Button disabled={index === visibleChildren.length - 1} onClick={handleNext}>
            {t('next', 'Next')}
          </Button>
          <Button disabled={index < visibleChildren.length - 1 || disableFinishButton} onClick={handleFinish}>
            {t('finish', 'Finish')}
          </Button>
        </div>
      </dialog>
    );
  };

  return ReactDOM.createPortal(createDialog(), document.body);
};

export default forwardRef(Wizard);
